import {
    CreateButton,
    DataTable,
    ExportButton,
    List,
    ReferenceField,
    SearchInput,
    SelectInput,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
    AlertOctagon,
    AlertTriangle,
    CheckCircle2,
    Circle,
    Clock
} from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { TopToolbar } from '../layout/TopToolbar';

const PRIORITY_CONFIG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
    low:      { label: 'Low',      color: 'bg-slate-100 text-slate-600 border-slate-200',  Icon: Circle },
    medium:   { label: 'Medium',   color: 'bg-blue-50 text-blue-700 border-blue-200',      Icon: AlertTriangle },
    high:     { label: 'High',     color: 'bg-amber-50 text-amber-700 border-amber-200',   Icon: AlertTriangle },
    critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300',        Icon: AlertOctagon },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
    open:        { label: 'Open',        color: 'bg-blue-50 text-blue-700 border-blue-200',       Icon: Circle },
    in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-200',   Icon: Clock },
    resolved:    { label: 'Resolved',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    closed:      { label: 'Closed',      color: 'bg-slate-100 text-slate-500 border-slate-200',   Icon: CheckCircle2 },
};

export const PriorityBadge = () => {
    const record = useRecordContext();
    if (!record?.priority) return null;
    const cfg = PRIORITY_CONFIG[record.priority] ?? PRIORITY_CONFIG.medium;
    const Icon = cfg.Icon;
    return (
        <Badge variant="outline" className={`text-[10px] font-bold ${cfg.color}`}>
            <Icon className="w-3 h-3 mr-1" />
            {cfg.label}
        </Badge>
    );
};

export const StatusBadgeST = () => {
    const record = useRecordContext();
    if (!record?.status) return null;
    const cfg = STATUS_CONFIG[record.status] ?? STATUS_CONFIG.open;
    const Icon = cfg.Icon;
    return (
        <Badge variant="outline" className={`text-[10px] font-bold ${cfg.color}`}>
            <Icon className="w-3 h-3 mr-1" />
            {cfg.label}
        </Badge>
    );
};

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
    <SelectInput
        key="status"
        source="status"
        choices={[
            { id: 'open', name: 'Open' },
            { id: 'in_progress', name: 'In Progress' },
            { id: 'resolved', name: 'Resolved' },
            { id: 'closed', name: 'Closed' },
        ]}
        alwaysOn
    />,
    <SelectInput
        key="priority"
        source="priority"
        choices={[
            { id: 'low', name: 'Low' },
            { id: 'medium', name: 'Medium' },
            { id: 'high', name: 'High' },
            { id: 'critical', name: 'Critical' },
        ]}
    />,
];

const ServiceTicketListActions = () => (
    <TopToolbar>
        <ExportButton />
        <CreateButton label="New Ticket" />
    </TopToolbar>
);

export const ServiceTicketList = () => (
    <List
        title="Service Tickets"
        actions={<ServiceTicketListActions />}
        filters={filters}
        filterDefaultValues={{ status: 'open' }}
        sort={{ field: 'created_at', order: 'DESC' }}
        className="p-6"
    >
        <Card className="border-none shadow-sm overflow-hidden">
            <DataTable rowClick="show">
                <DataTable.Col
                    source="ticket_number"
                    label="Ticket #"
                    className="font-mono font-bold text-blue-600"
                />
                <DataTable.Col source="title" label="Title" className="font-medium max-w-xs truncate" />
                <DataTable.Col label="Status">
                    <StatusBadgeST />
                </DataTable.Col>
                <DataTable.Col label="Priority">
                    <PriorityBadge />
                </DataTable.Col>
                <DataTable.Col source="category" label="Category" className="text-xs text-slate-500 capitalize" />
                <DataTable.Col label="Assigned To">
                    <ReferenceField source="assigned_to" reference="sales" link={false}>
                        <TextField source="first_name" className="text-sm" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col source="due_date" label="Due Date" className="text-sm" />
            </DataTable>
        </Card>
    </List>
);
